(() => {
  const initializedFlag = '__lumalandCollectionPinnedProductsInitialized';

  if (window[initializedFlag]) {
    return;
  }

  window[initializedFlag] = true;

  const configSelector = '[data-ll-pinned-products]';
  const configJsonSelector = 'script[data-ll-pinned-products-config]';
  const templateSelector = 'template[data-ll-pinned-products-template]';

  const managedAttribute = 'data-ll-pinned-product';
  const managedHandleAttribute = 'data-ll-pinned-handle';

  let syncTimer = null;

  /**
   * Normalize a Shopify product handle.
   */
  function normalizeHandle(value) {
    if (typeof value !== 'string') {
      return '';
    }

    return value.trim().toLowerCase();
  }

  /**
   * Extract product handle from /products/{handle}.
   */
  function getHandleFromHref(href) {
    if (!href) {
      return '';
    }

    try {
      const url = new URL(href, window.location.origin);
      const match = url.pathname.match(/\/products\/([^/]+)/i);

      return match
        ? normalizeHandle(decodeURIComponent(match[1]))
        : '';
    } catch (error) {
      return '';
    }
  }

  /**
   * Try several strategies to determine which product
   * a grid item belongs to.
   *
   * This is intentionally a little defensive because B4B
   * can alter the original product-card markup.
   */
  function getProductHandle(element) {
    if (!(element instanceof Element)) {
      return '';
    }

    const directHandle =
      element.getAttribute('handle') ||
      element.getAttribute('data-product-handle') ||
      element.getAttribute(managedHandleAttribute);

    if (directHandle) {
      return normalizeHandle(directHandle);
    }

    const productElement = element.querySelector(
      'product-card[handle], [data-product-handle], [data-ll-pinned-handle]'
    );

    if (productElement) {
      const nestedHandle =
        productElement.getAttribute('handle') ||
        productElement.getAttribute('data-product-handle') ||
        productElement.getAttribute(managedHandleAttribute);

      if (nestedHandle) {
        return normalizeHandle(nestedHandle);
      }
    }

    /*
     * Final fallback for B4B generated markup:
     * use a link pointing to /products/{handle}.
     */
    const productLink = element.matches('a[href*="/products/"]')
      ? element
      : element.querySelector('a[href*="/products/"]');

    return productLink
      ? getHandleFromHref(productLink.getAttribute('href'))
      : '';
  }

  /**
   * Read Liquid-generated configuration.
   */
  function readConfig(configElement) {
    const jsonElement = configElement.querySelector(configJsonSelector);

    if (!jsonElement) {
      return null;
    }

    try {
      const config = JSON.parse(jsonElement.textContent);

      const handles = Array.isArray(config.handles)
        ? config.handles
            .map(normalizeHandle)
            .filter(Boolean)
        : [];

      return {
        page: Number(config.page) || 1,
        handles: [...new Set(handles)]
      };
    } catch (error) {
      console.error(
        '[Lumaland Pins] Invalid configuration.',
        error
      );

      return null;
    }
  }

  /**
   * Product cards stored by Liquid inside the <template>.
   */
  function getTemplateProducts(configElement) {
    const template = configElement.querySelector(templateSelector);
    const productsByHandle = new Map();

    if (!template) {
      return productsByHandle;
    }

    Array.from(template.content.children).forEach((productElement) => {
      const handle = getProductHandle(productElement);

      if (handle && !productsByHandle.has(handle)) {
        productsByHandle.set(handle, productElement);
      }
    });

    return productsByHandle;
  }

  /**
   * Synchronize one product-list.
   */
  function syncProductList(configElement) {
    const config = readConfig(configElement);

    if (!config || config.handles.length === 0) {
      return;
    }

    const productListId =
      configElement.getAttribute('data-product-list-id');

    const productList = productListId
      ? document.getElementById(productListId)
      : configElement.parentElement?.querySelector(
          'product-list.product-list'
        );

    if (!productList) {
      return;
    }

    const pinnedHandles = new Set(config.handles);

    /*
     * Pages > 1:
     *
     * Do not insert the pinned products again.
     * Only remove their original/B4B versions so they
     * cannot appear a second time.
     */
    if (config.page !== 1) {
      Array.from(productList.children).forEach((item) => {
        const handle = getProductHandle(item);

        if (handle && pinnedHandles.has(handle)) {
          item.remove();
        }
      });

      return;
    }

    /*
     * Page 1:
     *
     * Keep track of cards already controlled by this script.
     * Every regular/B4B representation of a pinned product
     * is removed.
     */
    const templateProducts = getTemplateProducts(configElement);
    const managedProducts = new Map();

    Array.from(productList.children).forEach((item) => {
      const handle = getProductHandle(item);
      const isManaged = item.hasAttribute(managedAttribute);

      if (isManaged) {
        if (
          !handle ||
          !pinnedHandles.has(handle) ||
          managedProducts.has(handle)
        ) {
          item.remove();
          return;
        }

        managedProducts.set(handle, item);
        return;
      }

      /*
       * Remove original Shopify/B4B version of a pinned
       * product. This is the duplicate prevention.
       */
      if (handle && pinnedHandles.has(handle)) {
        item.remove();
      }
    });

    /*
     * Put pinned products at the beginning in exactly
     * the order defined in the Collection metafield.
     */
    let referenceNode = productList.firstElementChild;

    config.handles.forEach((handle) => {
      let productElement = managedProducts.get(handle);

      /*
       * B4B may have deleted our previously inserted
       * product. In that case restore it from the
       * Liquid <template>.
       */
      if (!productElement) {
        const templateProduct = templateProducts.get(handle);

        if (!templateProduct) {
          return;
        }

        productElement = templateProduct.cloneNode(true);

        productElement.setAttribute(
          managedAttribute,
          'true'
        );

        productElement.setAttribute(
          managedHandleAttribute,
          handle
        );

        /*
         * The card is dynamically inserted, therefore
         * don't use the normal initial reveal animation.
         */
        productElement.removeAttribute('reveal-on-scroll');
      }

      /*
       * insertBefore also moves an already existing node,
       * therefore this handles creation AND reordering.
       */
      if (productElement !== referenceNode) {
        productList.insertBefore(
          productElement,
          referenceNode
        );
      }

      referenceNode = productElement.nextElementSibling;
    });
  }

  /**
   * Synchronize every configured Collection on the page.
   */
  function syncAll() {
    document
      .querySelectorAll(configSelector)
      .forEach(syncProductList);
  }

  /**
   * B4B can perform a large number of DOM mutations at once.
   * Debounce them into a single synchronization.
   */
  function scheduleSync() {
    if (syncTimer !== null) {
      window.clearTimeout(syncTimer);
    }

    syncTimer = window.setTimeout(() => {
      syncTimer = null;
      syncAll();
    }, 60);
  }

  /**
   * We only care about changes affecting a Collection
   * or its product-list.
   */
  function mutationTouchesCollection(mutation) {
    const target = mutation.target instanceof Element
      ? mutation.target
      : mutation.target.parentElement;

    if (!target) {
      return false;
    }

    if (
      target.closest(
        'product-list.product-list, [data-ll-pinned-products]'
      )
    ) {
      return true;
    }

    const section = target.matches('.shopify-section')
      ? target
      : target.closest('.shopify-section');

    return Boolean(
      section?.querySelector('product-list.product-list')
    );
  }

  function start() {
    /*
     * Initial Shopify output.
     */
    syncAll();

    /*
     * Watch B4B as well as Shopify Section Rendering.
     *
     * This is the important part for your theme:
     * main-collection.liquid is replaced dynamically after
     * sorting/filtering and B4B can subsequently rebuild
     * the product list again.
     */
    const observer = new MutationObserver((mutations) => {
      if (mutations.some(mutationTouchesCollection)) {
        scheduleSync();
      }
    });

    observer.observe(document.documentElement, {
      childList: true,
      subtree: true
    });

    /*
     * One additional synchronization after all normal
     * window load handlers have run.
     */
    window.addEventListener(
      'load',
      scheduleSync,
      { once: true }
    );
  }

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      start,
      { once: true }
    );
  } else {
    start();
  }
})();